import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../axiosSetup";
import moment from "moment";
import CustomNavbar from "../CustomNavbar";
import DataTable from "./DataTable";
import cfg from "../../config";

const DEFAULT_HIDDEN = ["Besenpflicht?", "Nachtumzug", "Anzahl Busplätze", "Alkohol verboten"];

const formatFieldValue = (field) => {
  if (!field || field.value === null || field.value === undefined) return null;
  switch (field.dataType) {
    case "boolean":
      return field.value ? "Ja" : "Nein";
    case "date":
      return moment(field.value).isValid() ? moment(field.value).format("DD.MM.YYYY") : field.value;
    case "time":
      return moment(String(field.value), "HH:mm:ss").isValid()
        ? moment(String(field.value), "HH:mm:ss").format("HH:mm") + " Uhr"
        : field.value;
    case "datetime":
      return moment(field.value).isValid() ? moment(field.value).format("DD.MM.YYYY HH:mm") + " Uhr" : field.value;
    case "departures":
      return Array.isArray(field.value) && field.value.length > 0
        ? field.value.map((d) => `${d.label}: ${d.time} Uhr`).join(" | ")
        : null;
    default:
      if (Array.isArray(field.value) || (typeof field.value === "object" && field.value !== null)) return null;
      return field.value;
  }
};

const ZRMoveList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteMove, setDeleteMove] = useState(null);

  useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message);
      window.history.replaceState({}, document.title);
    }
    fetchMoves();
  }, []);

  const fetchMoves = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const res = await api.get("/api/umzugListe-zunftrat", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (Array.isArray(res.data)) setMoves(res.data);
      setError(null);
    } catch (err) {
      setError(err.message || `Fehler beim Laden der ${cfg.eventTermPlural}.`);
    } finally {
      setLoading(false);
    }
  };

  // Flatten moves to { _id, _move, _hasBookings, [frontendName]: fieldObj }
  const flatMoves = useMemo(
    () =>
      moves.map((move) => {
        const obj = { _id: move.id, _move: move, _hasBookings: move.hasBookings };
        (move.fields || []).forEach((f) => {
          obj[f.frontendName] = f;
        });
        return obj;
      }),
    [moves]
  );

  // Derive column list from all moves
  const allColNames = useMemo(() => {
    const set = new Set();
    moves.forEach((m) => (m.fields || []).forEach((f) => set.add(f.frontendName)));
    return Array.from(set);
  }, [moves]);

  const columns = useMemo(
    () =>
      allColNames.map((name) => ({
        key: name,
        label: name,
        render: (fieldObj) => {
          const val = formatFieldValue(fieldObj);
          if (val == null) return <span className="dt-muted">—</span>;
          if (fieldObj?.dataType === "boolean") {
            return fieldObj.value
              ? <span className="dt-badge dt-badge-yes">Ja</span>
              : <span className="dt-badge dt-badge-no">Nein</span>;
          }
          if (name === "Busplätze bebuchbar ab" && fieldObj?.value) {
            const d = moment(fieldObj.value);
            const isActive = d.isValid() && d.isSameOrBefore(moment());
            return (
              <span style={{
                display: "inline-block", whiteSpace: "nowrap",
                padding: "2px 8px", borderRadius: 999, fontSize: 12, fontWeight: 600,
                background: isActive ? "#16532222" : "#1e293b",
                color: isActive ? "#4ade80" : "#94a3b8",
                border: `1px solid ${isActive ? "#4ade80" : "#334155"}`,
              }}>
                {d.isValid() ? d.format("DD.MM.YY HH:mm") : val}
              </span>
            );
          }
          return val;
        },
        searchValue: (fieldObj) => {
          if (!fieldObj) return "";
          const formatted = formatFieldValue(fieldObj);
          if (formatted == null) return "";
          return String(formatted);
        },
      })),
    [allColNames]
  );

  const handleDeleteMove = async () => {
    if (!deleteMove) return;
    try {
      const token = localStorage.getItem("authToken");
      await api.delete(`/api/delete-move/${deleteMove._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowDeleteModal(false);
      setMessage(`${cfg.eventTerm} erfolgreich gelöscht.`);
      fetchMoves();
    } catch (err) {
      setError(`Fehler beim Löschen: ${cfg.eventTerm} konnte nicht entfernt werden.`);
    }
  };

  const actions = (row) => (
    <div className="dt-actions">
      <button
        className="dt-btn dt-btn-secondary"
        onClick={() => navigate("/zr-move-edit", { state: { move: row._move } })}
      >
        Bearbeiten
      </button>
      {row._hasBookings && (
        <button
          className="dt-btn dt-btn-success"
          onClick={() => navigate("/zr-move-booking-analysis", { state: { move: row._move } })}
        >
          Buchungen
        </button>
      )}
      <button
        className="dt-btn dt-btn-danger"
        onClick={() => { setDeleteMove(row); setShowDeleteModal(true); }}
      >
        Löschen
      </button>
    </div>
  );

  const headerActions = (
    <button
      className="dt-btn dt-btn-success"
      onClick={() => navigate("/zr-move-edit", { state: { mode: "create" } })}
    >
      + {cfg.eventTerm} erstellen
    </button>
  );

  return (
    <>
      <CustomNavbar />
      <div className="container-fluid px-3 px-md-4 mt-4">
        {message && (
          <div className="alert alert-success alert-dismissible mb-3" role="alert">
            {message}
            <button type="button" className="btn-close" onClick={() => setMessage(null)} />
          </div>
        )}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="mb-0">{cfg.eventTermPlural} Übersicht</h2>
        </div>

        <DataTable
          data={flatMoves}
          columns={columns}
          loading={loading}
          error={error}
          actions={actions}
          headerActions={headerActions}
          storageKey="zrmovelist_hidden_cols"
          defaultHiddenCols={DEFAULT_HIDDEN}
          searchPlaceholder={`${cfg.eventTerm} suchen…`}
          emptyText={`Keine ${cfg.eventTermPlural} vorhanden.`}
          initialPageSize={25}
          pageSizeOptions={[10, 25, 50]}
        />
        <br />
      </div>

      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-box animate-in" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title text-dark">{cfg.eventTerm} löschen</h5>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>&times;</button>
            </div>
            <div className="modal-body text-dark">
              <p>
                Bist du sicher, dass du folgenden Eintrag{" "}
                <strong>
                  {deleteMove?._move?.fields?.find((f) => f.key === "city")?.value || "Unbekannt"}
                </strong>{" "}
                löschen möchtest?
                <br />
                Diese Aktion kann nicht rückgängig gemacht werden.
              </p>
            </div>
            <div className="modal-footer mt-4 d-flex gap-2 justify-content-end">
              <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Abbrechen</button>
              <button className="btn btn-danger" onClick={handleDeleteMove}>Löschen</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ZRMoveList;
